import { Text } from '@sitecore-jss/sitecore-jss-react';
import { compose } from 'ramda'
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import Icon from '_components/Icon';
import { withCoveoNoIndexTags } from '_containers/BaseComponent';
import { getPortal, Portals } from '_utils/helpers';

import { SiteWideAnnouncementProps } from '../definitions';

import {
  ButtonWrapper,
  AnnouncementCTA,
  SecondaryButton,
  AnnouncementWrapper,
  CollapseWrapper,
  DISMISS_DURATION_MS,
  TitleWrapper,
  Content,
  TextWrapper,
  IconWrapper,
  Trigger,
} from './StyledSiteWideAnnouncement';

const Icons = {
  Blue: <Icon name="check_circle" />,
  Yellow: <Icon name="warning_amber"/>,
  Red: <Icon name="error_outline" />
};

const SiteWideAnnouncement: React.FC<SiteWideAnnouncementProps> = ({
  description,
  dismissable,
  id,
  link,
  secondaryLink,
  title,
  type,
  setAnnouncementDismissed,
}) => {
  const [t] = useTranslation();
  // `dismissing` drives the collapse animation, `removed` unmounts once it has
  // finished. Unmounting straight away (as this used to) snaps the page up.
  const [dismissing, setDismissing] = useState(false);
  const [removed, setRemoved] = useState(false);
  const collapseRef = useRef<HTMLDivElement>(null);

  const handleClickDismiss = () => {
    setDismissing(true);

    if (setAnnouncementDismissed) {
      setAnnouncementDismissed();
    }

    // Focus would otherwise fall back to <body> when the close button goes
    // away, losing keyboard and screen reader users' place on the page.
    const firstHeadingOne: null | HTMLHeadElement = document.querySelector('h1');

    if (firstHeadingOne !== null) {
      firstHeadingOne.tabIndex = -1;
      firstHeadingOne.focus();
    }
  };

  useEffect(() => {
    if (!dismissing) {
      return;
    }

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    const collapse = collapseRef.current;

    if (collapse && !prefersReducedMotion) {
      // `height` can't transition out of `auto`, so pin the measured height on
      // first, read it back to force the browser to lay that out as its own
      // step, then transition to zero. Without the read both writes land in the
      // same frame and the browser skips straight to the end state.
      collapse.style.height = `${collapse.offsetHeight}px`;
      void collapse.offsetHeight;
      collapse.style.height = '0px';
    }

    // `transitionend` never fires when the user has reduced motion on, so drive
    // the unmount off a timer instead and skip the wait in that case.
    const timer = window.setTimeout(
      () => setRemoved(true),
      prefersReducedMotion ? 0 : DISMISS_DURATION_MS
    );

    return () => window.clearTimeout(timer);
  }, [dismissing]);

  const portal = getPortal(Portals.before);

  if (removed || portal === null) {
    return null;
  }

  const hasDescription = description?.value !== '';

  return createPortal(
    <CollapseWrapper ref={collapseRef} isDismissing={dismissing} aria-hidden={dismissing}>
      <AnnouncementWrapper
        role="alert"
        alertStyle={type?.value || 'Yellow'}
        hasDescription={hasDescription}
        id={id}
        className={`${dismissing ? 'announcement-dismissing' : ''} site-wide-announcement`}
      >
        <IconWrapper>{Icons[type?.value || 'Yellow']}</IconWrapper>

        <TextWrapper>
          <TitleWrapper>
            <Text field={title} editable={false} />
          </TitleWrapper>

          {hasDescription && (
            <Content field={description} editable={false} tabIndex={dismissing ? -1 : null} />
          )}

          <ButtonWrapper>

          <AnnouncementCTA
            field={link}
            fallbackTitle={link?.fallbackTitle}
            linkSize="s"
            linkTheme="none"
          />

          <SecondaryButton
            field={secondaryLink}
            fallbackTitle={secondaryLink?.fallbackTitle}
            linkSize="s"
            linkTheme="none"
          />
          </ButtonWrapper>
        </TextWrapper>

        {
          (dismissable?.value ?? false) && (
            <Trigger
              type="button"
              aria-label={`${t('accessibility-close')} ${title?.value}`}
              aria-controls={id}
              aria-expanded={!dismissing}
              onClick={handleClickDismiss}
              tabIndex={dismissing ? -1 : null}
            >
              <Icon name="close" ariaHidden />
            </Trigger>
          )
        }
      </AnnouncementWrapper>
    </CollapseWrapper>,
    portal
  );
};

export default compose(withCoveoNoIndexTags)(SiteWideAnnouncement);
